import { useMemo, useState } from 'react';
import AppShell from '../components/AppShell';
import { reportsApi, type BatchExportLocation } from '../services/api';
import axios from 'axios';
import * as XLSX from 'xlsx';

type ParseResult = {
  locations: BatchExportLocation[];
  errors: string[];
  counts: {
    total: number;
    addresses: number;
    coordinates: number;
    placeIds: number;
  };
};

const COORDINATE_REGEX = /^-?\d+(?:\.\d+)?\s*,\s*-?\d+(?:\.\d+)?$/u;
const CHUNK_SIZE = 10;

const extractDetail = (raw: string): string | null => {
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (typeof parsed.detail === 'string') {
      return parsed.detail;
    }
  } catch {
    return null;
  }
  return null;
};

const parseLocations = (raw: string): ParseResult => {
  const lines = raw.split(/\r?\n/u);
  const locations: BatchExportLocation[] = [];
  const errors: string[] = [];
  let addresses = 0;
  let coordinates = 0;
  let placeIds = 0;

  lines.forEach((line, index) => {
    const trimmed = line.trim();
    if (!trimmed) return;

    const parts = trimmed.split('|');
    const name = parts.length > 1 ? (parts.shift()?.trim() ?? undefined) : undefined;
    const payload = parts.join('|').trim() || trimmed;
    const lineLabel = `Line ${String(index + 1)}`;

    if (!payload) {
      errors.push(`${lineLabel}: missing address or coordinates.`);
      return;
    }

    if (/^place_id:/i.test(payload)) {
      const value = payload.replace(/^place_id:/i, '').trim();
      if (!value) {
        errors.push(`${lineLabel}: missing place_id value.`);
        return;
      }
      locations.push({ name, place_id: value });
      placeIds += 1;
      return;
    }

    if (COORDINATE_REGEX.test(payload)) {
      const [latRaw, lngRaw] = payload.split(',').map((item) => item.trim());
      const latitude = Number(latRaw);
      const longitude = Number(lngRaw);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        errors.push(`${lineLabel}: invalid coordinates.`);
        return;
      }
      locations.push({ name, latitude, longitude });
      coordinates += 1;
      return;
    }

    locations.push({ name, address: payload });
    addresses += 1;
  });

  return {
    locations,
    errors,
    counts: {
      total: locations.length,
      addresses,
      coordinates,
      placeIds
    }
  };
};

const BatchExportPage = () => {
  const [rawInput, setRawInput] = useState('');
  const [reverseGeocode, setReverseGeocode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);

  const parsed = useMemo(() => parseLocations(rawInput), [rawInput]);
  const overLimit = parsed.counts.total > 100;

  const handleExport = async () => {
    setError(null);
    setSuccess(null);
    setProgress(null);

    if (parsed.errors.length > 0) {
      setError('Fix the highlighted lines before exporting.');
      return;
    }
    if (parsed.counts.total === 0) {
      setError('Paste at least one address or coordinate.');
      return;
    }
    if (overLimit) {
      setError('Batch export is limited to 100 locations per run.');
      return;
    }

    setLoading(true);
    try {
      const filename = 'gridfield-batch-metrics.xlsx';
      if (parsed.counts.total > CHUNK_SIZE) {
        const chunks: BatchExportLocation[][] = [];
        for (let i = 0; i < parsed.locations.length; i += CHUNK_SIZE) {
          chunks.push(parsed.locations.slice(i, i + CHUNK_SIZE));
        }
        const rows: (string | number | boolean | null)[][] = [];
        let header: (string | number | boolean | null)[] | null = null;
        for (let index = 0; index < chunks.length; index += 1) {
          setProgress(`Processing chunk ${String(index + 1)} of ${String(chunks.length)}...`);
          const response = await reportsApi.batchExport({
            locations: chunks[index],
            reverse_geocode: reverseGeocode
          });
          const buffer = await response.blob.arrayBuffer();
          const workbook = XLSX.read(buffer, { type: 'array' });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const data = XLSX.utils.sheet_to_json<(string | number | boolean | null)[]>(sheet, {
            header: 1,
            blankrows: false
          });
          if (!data.length) continue;
          header ??= data[0] ?? null;
          rows.push(...data.slice(1));
        }
        if (!header) {
          throw new Error('No rows returned from batch export.');
        }
        const outBook = XLSX.utils.book_new();
        const outSheet = XLSX.utils.aoa_to_sheet([header, ...rows]);
        XLSX.utils.book_append_sheet(outBook, outSheet, 'metrics');
        const outBuffer = XLSX.write(outBook, { bookType: 'xlsx', type: 'array' });
        const outBlob = new Blob([outBuffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = URL.createObjectURL(outBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
        setSuccess(`Export ready — downloaded ${filename}`);
      } else {
        const response = await reportsApi.batchExport({
          locations: parsed.locations,
          reverse_geocode: reverseGeocode
        });
        const url = URL.createObjectURL(response.blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
        setSuccess(`Export ready — downloaded ${response.filename}`);
      }
    } catch (err) {
      let message = 'Unable to generate the batch export. Please try again.';
      if (axios.isAxiosError(err)) {
        const response = err.response;
        if (response?.data instanceof Blob) {
          const text = await response.data.text();
          const detail = extractDetail(text);
          if (detail) {
            message = detail;
          }
        } else {
          const data = response?.data as Record<string, unknown> | undefined;
          if (typeof data?.detail === 'string') {
            message = data.detail;
          }
        }
        if (response?.status === 403) {
          message = 'This account is not enabled for batch exports.';
        } else if (response?.status && response.status >= 500) {
          message = 'Batch export failed on the server. Please retry or reduce the batch size.';
        }
      }
      setError(message);
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  return (
    <AppShell title="Batch export">
      <section className="grid gap-8 lg:grid-cols-[2fr,1fr]">
        <div className="rounded-[28px] border border-black/5 bg-white/90 p-6 shadow-[0_20px_60px_rgba(0,0,0,0.05)] lg:p-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.35em] text-[#8a8b99]">Batch metrics export</p>
              <h1 className="mt-3 text-2xl font-semibold text-[#0f0f19]">Paste locations to export</h1>
              <p className="mt-2 max-w-2xl text-sm text-[#5a5b6c]">
                Paste up to 100 addresses or coordinates. We will geocode the locations, run the report pipeline, and
                return an Excel file with scores and infrastructure metrics.
              </p>
            </div>
            <div className="rounded-full border border-black/10 bg-[#f5f5f5] px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-[#0f0f19]">
              {parsed.counts.total}/100
            </div>
          </div>

          <div className="mt-6 space-y-4">
            <textarea
              value={rawInput}
              onChange={(event) => setRawInput(event.target.value)}
              placeholder={`Berlin, Alexanderplatz\\nHamburg, Germany\\nName | 52.5200, 13.4050\\nplace_id: ChIJAVkDPzdOqEcRcDteW0YgIQQ`}
              rows={12}
              className="w-full rounded-[24px] border border-black/10 bg-white px-4 py-4 text-sm text-[#0f0f19] placeholder-[#b0b0bf] shadow-[inset_0_1px_8px_rgba(0,0,0,0.04)] focus:border-black/30 focus:outline-none focus:ring-2 focus:ring-black/10"
            />
            <label className="flex items-center gap-3 rounded-2xl border border-black/5 bg-[#f7f7f7] px-4 py-3 text-xs font-semibold uppercase tracking-[0.28em] text-[#5a5b6c]">
              <input
                type="checkbox"
                checked={reverseGeocode}
                onChange={(event) => setReverseGeocode(event.target.checked)}
                className="h-4 w-4 rounded border-black/20 text-[#0f0f19] focus:ring-black/30"
              />
              Reverse geocode coordinate lines
            </label>
          </div>

          {parsed.errors.length > 0 && (
            <div className="mt-4 rounded-2xl border border-amber-500/30 bg-amber-50 px-4 py-3 text-xs text-amber-700">
              {parsed.errors.slice(0, 4).map((entry) => (
                <p key={entry}>{entry}</p>
              ))}
              {parsed.errors.length > 4 && <p>+ {parsed.errors.length - 4} more line warnings</p>}
            </div>
          )}
          {error && (
            <div className="mt-4 rounded-2xl border border-red-500/30 bg-red-50 px-4 py-3 text-sm text-red-600">
              {error}
            </div>
          )}
          {success && (
            <div className="mt-4 rounded-2xl border border-emerald-500/30 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              {success}
            </div>
          )}
          {progress && (
            <div className="mt-4 rounded-2xl border border-blue-500/30 bg-blue-50 px-4 py-3 text-sm text-blue-700">
              {progress}
            </div>
          )}

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={handleExport}
              disabled={loading || parsed.counts.total === 0 || parsed.errors.length > 0 || overLimit}
              className="flex-1 rounded-full bg-[#0f0f19] px-6 py-2.5 text-[11px] font-semibold uppercase tracking-[0.35em] text-white shadow-[0_16px_35px_rgba(0,0,0,0.17)] transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? 'Exporting…' : 'Export metrics'}
            </button>
            <button
              type="button"
              onClick={() => {
                setRawInput('');
                setError(null);
                setSuccess(null);
              }}
              className="flex-1 rounded-full border border-black/15 px-6 py-2.5 text-[11px] font-semibold uppercase tracking-[0.35em] text-[#5a5b6c] transition hover:border-black/30 hover:text-[#0f0f19]"
            >
              Clear input
            </button>
          </div>
        </div>

        <aside className="space-y-6">
          <div className="rounded-[26px] border border-black/5 bg-white/80 p-6 shadow-[0_16px_45px_rgba(0,0,0,0.05)]">
            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-[#8a8b99]">Input formats</p>
            <ul className="mt-4 space-y-3 text-sm text-[#5a5b6c]">
              <li>Address per line: <span className="font-semibold text-[#0f0f19]">Berlin, Alexanderplatz</span></li>
              <li>Name + address: <span className="font-semibold text-[#0f0f19]">Site A | Frankfurt am Main</span></li>
              <li>Coordinates: <span className="font-semibold text-[#0f0f19]">52.52, 13.405</span></li>
              <li>Place ID: <span className="font-semibold text-[#0f0f19]">place_id: ChIJ...</span></li>
            </ul>
          </div>

          <div className="rounded-[26px] border border-black/5 bg-white/80 p-6 shadow-[0_16px_45px_rgba(0,0,0,0.05)]">
            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-[#8a8b99]">Batch summary</p>
            <div className="mt-4 grid gap-3 text-sm">
              <div className="flex items-center justify-between rounded-2xl border border-black/5 bg-[#f5f5f5] px-4 py-3">
                <span>Addresses</span>
                <span className="font-semibold text-[#0f0f19]">{parsed.counts.addresses}</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl border border-black/5 bg-[#f5f5f5] px-4 py-3">
                <span>Coordinates</span>
                <span className="font-semibold text-[#0f0f19]">{parsed.counts.coordinates}</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl border border-black/5 bg-[#f5f5f5] px-4 py-3">
                <span>Place IDs</span>
                <span className="font-semibold text-[#0f0f19]">{parsed.counts.placeIds}</span>
              </div>
            </div>
            {overLimit && (
              <p className="mt-3 text-xs text-amber-600">
                Limit reached. Reduce to 100 locations or fewer.
              </p>
            )}
          </div>

          {parsed.locations.length > 0 && (
            <div className="rounded-[26px] border border-black/5 bg-white/80 p-6 shadow-[0_16px_45px_rgba(0,0,0,0.05)]">
              <p className="text-xs font-semibold uppercase tracking-[0.35em] text-[#8a8b99]">Preview</p>
              <ul className="mt-4 space-y-3 text-sm text-[#5a5b6c]">
                {parsed.locations.slice(0, 6).map((entry, idx) => (
                  <li key={`${entry.name ?? entry.address ?? entry.place_id ?? 'coord'}-${String(idx)}`} className="rounded-2xl border border-black/5 bg-white px-4 py-3">
                    <p className="font-semibold text-[#0f0f19]">{entry.name ?? 'Untitled'}</p>
                    {entry.address && <p>{entry.address}</p>}
                    {entry.place_id && <p className="text-xs text-[#8a8b99]">place_id: {entry.place_id}</p>}
                    {entry.latitude !== undefined && entry.longitude !== undefined && (
                      <p className="text-xs text-[#8a8b99]">
                        {entry.latitude.toFixed(4)}, {entry.longitude.toFixed(4)}
                      </p>
                    )}
                  </li>
                ))}
                {parsed.locations.length > 6 && (
                  <li className="text-xs text-[#8a8b99]">+ {parsed.locations.length - 6} more locations</li>
                )}
              </ul>
            </div>
          )}
        </aside>
      </section>
    </AppShell>
  );
};

export default BatchExportPage;
